import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Grid, TableRow, TableHead, TableContainer, TableCell, TableBody, Table, Button, makeStyles } from '@material-ui/core';
import { RootStore } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'components/Loader';
import SaveIcon from '@material-ui/icons/Save';
import { LoaderButton } from 'components/LoaderButton';
import { TeamUsers as TeamUsersObject } from 'redux/models/teamUsers';
import { TeamUserRow } from '../TeamUserRow';
import { updateUsers } from 'redux/thunks';
import { resetUsersDeletingSuccess, resetUsersUpdatingSuccess } from 'redux/actions';
import { EditTeamUsers } from 'redux/models/editTeamUsers';
import { deleteUsers } from 'redux/thunks/user';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { AddTeamMembersModal } from 'components/Modals/AddTeamMembersModal';
import './TeamUsers.scss';
import { orderByProperty } from 'helpers';
import { Alert } from '@material-ui/lab';

interface Action {
	type: string;
	teams?: TeamUsersObject[];
	index?: number;
	userId?: string;
}

const useStyles = makeStyles({
	addMembersRoot: {
		backgroundColor: 'white',
		borderColor: 'white',
	},
	saveRoot: {
		marginRight: '1rem',
	},
});

// reducer used to manage the team user state
function reducer(state: EditTeamUsers[], action: Action) {
	switch (action.type) {
		case 'INITIAL_LOAD':
			let loadState = [...state];
			if (action.teams) {
				loadState = action.teams.map(team => {
					return {
						...team,
						isDeleted: false,
					};
				});
			}
			return loadState;
		case 'SET_ADMIN':
			return state.map((teamUser, index) => {
				if (index === action.index) {
					return { ...teamUser, isAdmin: true };
				} else {
					return teamUser;
				}
			});
		case 'SET_MEMBER':
			return state.map((teamUser, index) => {
				if (index === action.index) {
					return { ...teamUser, isAdmin: false };
				} else {
					return teamUser;
				}
			});
		case 'SET_DELETED':
			return state.map(teamUser => {
				if (teamUser.userId === action.userId) {
					return { ...teamUser, isDeleted: true };
				} else {
					return teamUser;
				}
			});
		case 'SET_ACTIVE':
			return state.map(teamUser => {
				if (teamUser.userId === action.userId) {
					return { ...teamUser, isDeleted: false };
				} else {
					return teamUser;
				}
			});
		default:
			return state;
	}
}

const initialState: EditTeamUsers[] = [];

const TeamUsers: React.FC = () => {
	const users = useSelector((state: RootStore) => state.user.users);
	const usersLoading = useSelector((state: RootStore) => state.user.usersLoading);
	const usersUpdating = useSelector((state: RootStore) => state.user.usersUpdating);
	const usersUpdatingSuccess = useSelector((state: RootStore) => state.user.usersUpdatingSuccess);
	const usersDeleting = useSelector((state: RootStore) => state.user.usersDeleting);
	const usersDeletingSuccess = useSelector((state: RootStore) => state.user.usersDeletingSuccess);
	const selectedTeam = useSelector((state: RootStore) => state.user.selectedTeam);
	const userId = useSelector((state: RootStore) => state.user.userId);
	const [teamUsers, teamUsersDispatch] = useReducer(reducer, initialState);
	const [teamUsersLoading, setTeamUsersLoading] = useState(false);
	const [addTeamMembersModalOpen, setAddTeamMembersModalOpen] = useState(false);
	let usersToUpdate: any = useRef({});
	const storeDispatch = useDispatch();
	const classes = useStyles();

	const onViewAddTeamMembersModalClicked = () => {
		setAddTeamMembersModalOpen(true);
	};

	const onViewAddTeamMembersModalClosed = () => {
		setAddTeamMembersModalOpen(false);
	};

	// click action for removing a user
	const removeUserClick = (teamUser: EditTeamUsers, deleted: boolean) => {
		if (deleted) {
			teamUsersDispatch({
				type: 'SET_DELETED',
				userId: teamUser.userId,
			});
		} else {
			teamUsersDispatch({
				type: 'SET_ACTIVE',
				userId: teamUser.userId,
			});
		}

		updateUserIsDeleted(teamUser, deleted);
	};

	// handles when a new role is selected for a user
	const handleInputChange = useCallback(
		(event: any) => {
			const value = event.target.value;
			const index = event.target.id;
			if (value === 'Administrator') {
				updateUserIsAdmin(teamUsers[index], true);
				teamUsersDispatch({
					type: 'SET_ADMIN',
					index: parseInt(index),
				});
			} else {
				updateUserIsAdmin(teamUsers[index], false);
				teamUsersDispatch({
					type: 'SET_MEMBER',
					index: parseInt(index),
				});
			}
		},
		[teamUsers]
	);

	// updates the ref containing the users to update for setting a user to deleted
	const updateUserIsDeleted = (teamUser: EditTeamUsers, isDeleted: boolean) => {
		const existingUserToUpdate = usersToUpdate.current[teamUser.userId];

		if (!existingUserToUpdate) {
			usersToUpdate.current[teamUser.userId] = {
				original: teamUser,
				updated: { ...teamUser, isDeleted: isDeleted },
			};
		} else if (existingUserToUpdate.original.isDeleted === isDeleted && existingUserToUpdate.original.isAdmin === existingUserToUpdate.updated.isAdmin) {
			delete usersToUpdate.current[teamUser.userId];
		} else {
			usersToUpdate.current[teamUser.userId] = {
				...existingUserToUpdate,
				updated: { ...teamUser, isDeleted: isDeleted },
			};
		}
	};

	// updates the ref containing the users to update for setting a user to admin
	const updateUserIsAdmin = (teamUser: EditTeamUsers, isAdmin: boolean) => {
		const existingUserToUpdate = usersToUpdate.current[teamUser.userId];
		if (!existingUserToUpdate) {
			usersToUpdate.current[teamUser.userId] = {
				original: teamUser,
				updated: { ...teamUser, isAdmin: isAdmin },
			};
		} else if (existingUserToUpdate.original.isAdmin === isAdmin && existingUserToUpdate.original.isDeleted === existingUserToUpdate.updated.isDeleted) {
			delete usersToUpdate.current[teamUser.userId];
		} else {
			usersToUpdate.current[teamUser.userId] = {
				...existingUserToUpdate,
				updated: { ...teamUser, isAdmin: isAdmin },
			};
		}
	};

	// called on button click to save user updates
	const saveUserUpdates = () => {
		if (Object.keys(usersToUpdate.current).length !== 0 && selectedTeam?.teamId) {
			let updatedUsers: TeamUsersObject[] = [];
			let deleteIds: string[] = [];

			Object.entries(usersToUpdate.current).forEach(([key, value]: any) => {
				const updatedUser = value.updated;
				if (updatedUser.isDeleted) {
					deleteIds.push(updatedUser.userId);
				} else {
					updatedUsers.push(updatedUser);
				}
			});

			if (updatedUsers.length > 0) {
				storeDispatch(updateUsers(selectedTeam.teamId, updatedUsers));
			}
			if (deleteIds.length > 0) {
				storeDispatch(deleteUsers(selectedTeam.teamId, deleteIds));
			}
		}
	};

	// use effect for initial component render
	useEffect(() => {
		if (users.length !== 0) {
			setTeamUsersLoading(true);
			const initTeamUsers = [...users];
			teamUsersDispatch({
				type: 'INITIAL_LOAD',
				teams: orderByProperty(initTeamUsers, 'userName') as TeamUsersObject[],
			});
			usersToUpdate.current = {};
		}
	}, [users]);

	// use effect for resetting userUpdatingSuccess/usersDeletingSuccess if successful update, depends on usersUpdatingSuccess and usersDeletingSuccess
	useEffect(() => {
		if (usersUpdatingSuccess) {
			storeDispatch(resetUsersUpdatingSuccess());
		}
		if (usersDeletingSuccess) {
			storeDispatch(resetUsersDeletingSuccess());
		}
	}, [usersUpdatingSuccess, usersDeletingSuccess, storeDispatch]);

	// use effect for setting team users loading to false, depends on team users
	useEffect(() => {
		setTeamUsersLoading(false);
	}, [teamUsers]);

	// use effect for setting team users loading to true, depends on users loading
	useEffect(() => {
		setTeamUsersLoading(true);
	}, [usersLoading]);

	return (
		<Grid container direction='column' alignContent='center' alignItems='center'>
			{teamUsersLoading ? (
				<Loader />
			) : (
				<>
					{selectedTeam?.isAdmin && (
						<Alert severity='info' className='members-admin-info'>
							As an 'Administrator' of this team you can modify member roles and delete team members. Others are limited to viewing member roles
							and adding new members.
						</Alert>
					)}
					<div className='team-users__wrapper'>
						<Grid container>
							{selectedTeam?.isAdmin && (
								<LoaderButton
									classes={{
										root: classes.saveRoot,
									}}
									loading={usersUpdating || usersDeleting}
									disabled={Object.keys(usersToUpdate.current).length === 0 || !selectedTeam?.isAdmin}
									type='submit'
									startIcon={<SaveIcon />}
									onHandleClick={saveUserUpdates}
								>
									Save Changes
								</LoaderButton>
							)}
							<Button
								classes={{
									root: classes.addMembersRoot,
								}}
								variant='outlined'
								color='primary'
								startIcon={<PersonAddIcon />}
								onClick={onViewAddTeamMembersModalClicked}
								className='team-users__add-member'
							>
								Add Members
							</Button>
						</Grid>
						<Grid container direction='column' alignItems='center' className='team-users__container'>
							<TableContainer className='team-users__table-container'>
								<Table aria-label='simple table' className='team-users__table'>
									<TableHead className='team-users__header'>
										<TableRow className='team-users__header-row'>
											<TableCell className='team-users__header-row-item'>Name</TableCell>
											<TableCell className='team-users__header-row-item'>Email</TableCell>
											<TableCell className='team-users__header-row-item--resized'>Role</TableCell>
											{selectedTeam?.isAdmin && <TableCell className='team-users__header-row-item--resized'>Action</TableCell>}
										</TableRow>
									</TableHead>
									<TableBody className='team-users__body'>
										{teamUsers.map((value: EditTeamUsers, index: number) => (
											<TeamUserRow
												disabled={!selectedTeam?.isAdmin}
												key={index}
												index={index}
												userId={userId}
												teamUser={value}
												onChange={handleInputChange}
												removeUser={removeUserClick}
											/>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>
					</div>
				</>
			)}
			<AddTeamMembersModal
				open={addTeamMembersModalOpen}
				userId={userId}
				teamName={selectedTeam?.teamName || ''}
				teamId={selectedTeam?.teamId || ''}
				onCloseClicked={onViewAddTeamMembersModalClosed}
				allowClickOutsideToClose={true}
			/>
		</Grid>
	);
};

export default TeamUsers;
